import {
  type UseVirtualFloatingOptions,
  flip,
  offset,
} from "@platejs/floating";
import { getLinkAttributes } from "@platejs/link";
import {
  type LinkFloatingToolbarState,
  FloatingLinkUrlInput,
  useFloatingLinkEdit,
  useFloatingLinkEditState,
  useFloatingLinkInsert,
  useFloatingLinkInsertState,
} from "@platejs/link/react";
import { ExternalLink, Link, Text, Unlink } from "lucide-react";
import type { TLinkElement } from "platejs";
import { KEYS } from "platejs";
import {
  useEditorRef,
  useEditorSelection,
  useFormInputProps,
} from "platejs/react";
import { memo, useMemo } from "react";
import { cn } from "../../tailwind-utils";
import { buttonVariants } from "../ui/button";
import { Separator } from "../ui/separator";

const popoverClassName =
  "z-50 w-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-hidden";

const inputClassName =
  "flex h-[28px] w-full rounded-md border-none bg-transparent px-1.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-transparent";

export interface LinkFloatingToolbarProps {
  state?: LinkFloatingToolbarState;
}

export const LinkFloatingToolbar = memo(function LinkFloatingToolbar({
  state,
}: LinkFloatingToolbarProps) {
  const floatingOptions: UseVirtualFloatingOptions = useMemo(
    () => ({
      middleware: [
        offset(8),
        flip({
          fallbackPlacements: ["bottom-end", "top-start", "top-end"],
          padding: 12,
        }),
      ],
      placement: "bottom-start" as const,
    }),
    [],
  );

  const insertState = useFloatingLinkInsertState({
    ...state,
    floatingOptions: { ...floatingOptions, ...state?.floatingOptions },
  });
  const {
    hidden,
    props: insertProps,
    ref: insertRef,
    textInputProps,
  } = useFloatingLinkInsert(insertState);

  const editState = useFloatingLinkEditState({
    ...state,
    floatingOptions: { ...floatingOptions, ...state?.floatingOptions },
  });
  const {
    editButtonProps,
    props: editProps,
    ref: editRef,
    unlinkButtonProps,
  } = useFloatingLinkEdit(editState);

  const inputProps = useFormInputProps({ preventDefaultOnEnterKeydown: true });

  if (hidden) return null;

  const input = (
    <div className="flex w-[330px] flex-col" {...inputProps}>
      <div className="flex items-center">
        <div className="flex items-center pr-1 pl-2 text-muted-foreground">
          <Link className="size-4" />
        </div>
        <FloatingLinkUrlInput
          className={inputClassName}
          data-plate-focus
          placeholder="Paste link"
        />
      </div>
      <Separator className="my-1" />
      <div className="flex items-center">
        <div className="flex items-center pr-1 pl-2 text-muted-foreground">
          <Text className="size-4" />
        </div>
        <input
          className={inputClassName}
          data-plate-focus
          placeholder="Text to display"
          {...textInputProps}
        />
      </div>
    </div>
  );

  const editContent = editState.isEditing ? (
    input
  ) : (
    <div className="box-content flex items-center">
      <button
        className={cn(buttonVariants({ size: "sm", variant: "ghost" }))}
        type="button"
        {...editButtonProps}
      >
        Edit link
      </button>
      <Separator orientation="vertical" />
      <LinkOpenButton />
      <Separator orientation="vertical" />
      <button
        className={cn(buttonVariants({ size: "sm", variant: "ghost" }))}
        type="button"
        {...unlinkButtonProps}
      >
        <Unlink className="size-4" />
      </button>
    </div>
  );

  return (
    <>
      <div className={popoverClassName} ref={insertRef} {...insertProps}>
        {input}
      </div>
      <div className={popoverClassName} ref={editRef} {...editProps}>
        {editContent}
      </div>
    </>
  );
});

const LinkOpenButton = memo(function LinkOpenButton() {
  const editor = useEditorRef();
  const selection = useEditorSelection();

  const attributes = useMemo(() => {
    const entry = editor.api.node<TLinkElement>({
      match: { type: editor.getType(KEYS.link) },
    });
    if (!entry) {
      return {};
    }
    const [element] = entry;
    return getLinkAttributes(editor, element);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, selection]);

  return (
    <a
      {...attributes}
      aria-label="Open link in a new tab"
      className={cn(buttonVariants({ size: "sm", variant: "ghost" }))}
      onMouseOver={(e) => {
        e.stopPropagation();
      }}
      target="_blank"
    >
      <ExternalLink className="size-4" />
    </a>
  );
});
